/**
 * Component: SubscriptionManagement
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 * 
 * Purpose:
 * - Manages restaurant subscription billing and plan changes
 * - Displays current plan details and usage statistics
 * - Handles subscription cancellation with feedback capture
 * - Provides Stripe portal access for billing management
 * - Shows subscription status and billing dates
 * 
 * Props: None (uses API to fetch subscription data)
 * 
 * State Management:
 * - Subscription data from backend API
 * - Cancellation modal and reason tracking
 * - Processing states for async operations
 * 
 * Performance:
 * - Single API call on mount
 * - Optimistic UI updates during actions
 * - Debounced form inputs in cancellation flow
 * 
 * Error Handling:
 * - Toast notifications for all errors
 * - Graceful degradation for demo users
 * - Network retry logic for critical actions
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard,
  Calendar,
  Check,
  ArrowRight,
  AlertCircle,
  Loader2,
  Crown,
  ChefHat,
  Utensils,
  TrendingUp,
  Download,
  X,
  Sparkles,
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { stripeApi } from '@/services/api/stripeApi';
import { apiClient } from '@/services/api/apiClient';
import { usePlans, getFeatureDisplayText } from '@/hooks/usePlans';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

interface SubscriptionData {
  plan_name: string;
  billing_cycle: 'monthly' | 'quarterly' | 'annual';
  current_period_end: string;
  cancel_at_period_end: boolean;
  status: string;
  locations_used: number;
  ai_images_used: number;
  ai_images_limit: number;
  is_trial: boolean;
  trial_end?: string;
}

interface AIHostSubscriptionData {
  billing_period_start: string;
  billing_period_end: string;
  plan_name: string;
  plan_display_name: string;
  minutes_included: number;
  minutes_used: number;
  minutes_remaining: number;
  usage_percent: number;
  overage_minutes: number;
  overage_cost: number;
  transaction_count: number;
  transaction_fees: number;
  base_subscription_fee: number;
  estimated_bill: number;
  alerts: string[];
  account_pending?: boolean;  // True for pending_activation accounts
  message?: string;
}

const SubscriptionManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [aiHostSubscription, setAiHostSubscription] = useState<AIHostSubscriptionData | null>(null);
  const [processingAction, setProcessingAction] = useState(false);
  const { data: plansData } = usePlans();
  
  // Check if this is a demo user
  const isDemoUser = localStorage.getItem('isDemo') === 'true';

  useEffect(() => {
    fetchSubscription();

    // Auto-refresh when returning from Stripe portal
    const handleFocus = () => {
      // Only refresh if we've been away for more than 2 seconds
      // This prevents unnecessary API calls on quick focus changes
      const lastFetch = localStorage.getItem('lastSubscriptionFetch');
      const now = Date.now();

      if (!lastFetch || now - parseInt(lastFetch) > 2000) {
        console.log('[SubscriptionManagement] Page regained focus, refreshing subscription data');
        fetchSubscription();
      }
    };

    window.addEventListener('focus', handleFocus);

    // Also refresh when visibility changes (for mobile browsers)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchSubscription = async () => {
    try {
      console.log('[SubscriptionManagement] Fetching subscription data');

      // Fetch both subscription types in parallel
      const [menuSubResponse, aiHostSubResponse] = await Promise.allSettled([
        apiClient.get('/subscription/current'),
        apiClient.get('/virtual-host/control/usage')
      ]);

      // Handle menu subscription
      if (menuSubResponse.status === 'fulfilled') {
        setSubscription(menuSubResponse.value.data);
        console.log('[SubscriptionManagement] Menu subscription data:', menuSubResponse.value.data);
      } else {
        console.log('[SubscriptionManagement] No menu subscription found');
        setSubscription(null);
      }

      // Handle AI Host subscription
      if (aiHostSubResponse.status === 'fulfilled') {
        setAiHostSubscription(aiHostSubResponse.value.data);
        console.log('[SubscriptionManagement] AI Host subscription data:', aiHostSubResponse.value.data);
      } else {
        console.log('[SubscriptionManagement] No AI Host subscription found');
        setAiHostSubscription(null);
      }

      // Store timestamp for focus-based refresh logic
      localStorage.setItem('lastSubscriptionFetch', Date.now().toString());

      // Only show error if BOTH subscription types failed
      if (menuSubResponse.status === 'rejected' && aiHostSubResponse.status === 'rejected') {
        toast({
          title: "Error",
          description: "Failed to load subscription details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('[SubscriptionManagement] Failed to fetch subscriptions:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Generic handler for billing portal with optional flow type
  const openBillingPortal = async (flowType?: string) => {
    // For demo users, show a message instead of opening Stripe portal
    if (isDemoUser) {
      toast({
        title: "Demo Mode",
        description: "Billing management is not available in demo mode.",
      });
      return;
    }

    // Check for valid auth token
    const authToken = Cookies.get('auth_token');
    if (!authToken) {
      toast({
        title: "Authentication Required",
        description: "Billing portal requires a real Stripe customer. Complete a real signup flow to test this feature.",
      });
      return;
    }

    try {
      const { portal_url } = await stripeApi.createPortalSession(flowType);
      window.open(portal_url, '_blank');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open billing portal",
        variant: "destructive"
      });
    }
  };

  // Handler for "Manage Subscription" - opens subscription update/cancel flow
  const handleManageSubscription = () => openBillingPortal('subscription_update');

  // Handler for "Download Invoices" - opens default portal (billing history)
  const handleDownloadInvoices = () => openBillingPortal();

  // Legacy handler for backwards compatibility
  const handleManageBilling = () => openBillingPortal();

  // Cancellation is now handled through Stripe Customer Portal

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPlanIcon = (planName: string) => {
    switch (planName) {
      case 'yummy': return Utensils;
      case 'delicious': return ChefHat;
      case 'gourmet': return Crown;
      default: return Utensils;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName) {
      case 'yummy': return 'from-green-400 to-green-600';
      case 'delicious': return 'from-blue-400 to-blue-600';
      case 'gourmet': return 'from-purple-400 to-purple-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Show pending activation message if account is not yet activated
  if (aiHostSubscription?.account_pending) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Account Activation Pending</h3>
            <p className="text-gray-600 text-sm mb-3">
              Your account is being set up. Please check your email for the activation link to complete your registration.
            </p>
            <p className="text-gray-500 text-xs">
              Once you complete the activation process, your subscription details will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show "no subscription" only if BOTH subscription types are null
  if (!subscription && !aiHostSubscription) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Active Subscription</h3>
            <p className="text-gray-600 text-sm mb-3">
              Your subscription is being set up or may need activation.
            </p>
            <p className="text-gray-500 text-xs">
              If you recently signed up, please allow a few minutes for your subscription to be activated.
              For assistance, contact{' '}
              <a href="mailto:support@dineai.ai" className="text-[#1f4ed1] hover:underline">
                support@dineai.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const PlanIcon = subscription ? getPlanIcon(subscription.plan_name) : null;
  const planGradient = subscription ? getPlanColor(subscription.plan_name) : '';
  const currentPlan = subscription ? plansData?.plans.find(p => p.name === subscription.plan_name) : null;

  return (
    <>
      <div className="space-y-6">
        {/* Menu Subscription (Virtual Assistant) */}
        {subscription && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${planGradient} flex items-center justify-center`}>
                {PlanIcon && <PlanIcon className="w-8 h-8 text-white" />}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900 capitalize">
                    {subscription.plan_name} Plan
                  </h2>
                  {subscription.is_trial && (
                    <span className="px-3 py-1 text-sm bg-[#FDB913]/20 text-[#b8860b] rounded-full font-medium animate-pulse">
                      30-Day Trial
                    </span>
                  )}
                </div>
                <p className="text-gray-500">
                  {subscription.billing_cycle === 'monthly' ? 'Monthly' :
                   subscription.billing_cycle === 'quarterly' ? 'Quarterly' : 'Annual'} billing
                </p>
              </div>
            </div>

            {subscription.cancel_at_period_end && (
              <div className="bg-red-100 border border-red-200 rounded-lg px-3 py-1">
                <p className="text-red-600 text-sm font-medium">Cancelling</p>
              </div>
            )}
          </div>

          {/* Billing Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">
                {subscription.is_trial ? 'Trial ends' : 'Next billing date'}
              </p>
              <p className="text-gray-900 text-lg font-medium">
                {formatDate(subscription.trial_end || subscription.current_period_end)}
              </p>
              {subscription.is_trial && (
                <p className="text-[#b8860b] text-sm mt-1">
                  {(() => {
                    const daysLeft = Math.ceil((new Date(subscription.trial_end || subscription.current_period_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    return `${daysLeft} days remaining in trial`;
                  })()}
                </p>
              )}
            </div>

            <div>
              <p className="text-gray-500 text-sm mb-1">Status</p>
              <p className="text-gray-900 text-lg font-medium capitalize">
                {subscription.status}
              </p>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-gray-500 text-sm mb-1">Locations</p>
              <p className="text-gray-900 font-medium">
                {subscription.locations_used} of {currentPlan?.features.locationsIncluded || 1} used
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">AI Images (per year)</p>
              <p className="text-gray-900 font-medium">
                {subscription.ai_images_used} of {subscription.ai_images_limit} used
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-6">
            <Button
              onClick={handleManageSubscription}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Manage Subscription
            </Button>

            {!subscription.cancel_at_period_end ? (
              <>
                {subscription.plan_name !== 'gourmet' && (
                  <Button
                    onClick={() => {
                      if (isDemoUser) {
                        // For demo users, navigate to plan selector
                        navigate('/demo/select-plan');
                      } else {
                        // For real users, navigate to plan selection with upgrade mode
                        navigate('/plan-selection?mode=upgrade');
                      }
                    }}
                    className="bg-[#FDB913] hover:bg-[#FDB913]/90 text-black"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={handleManageSubscription}
                className="bg-green-500 hover:bg-green-400 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Reactivate Subscription
              </Button>
            )}
          </div>
        </div>
        )}

        {/* AI Host Subscription */}
        {aiHostSubscription && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {aiHostSubscription.plan_display_name || 'AI Host'}
                </h2>
                <p className="text-gray-500">
                  Virtual Phone Hostess
                </p>
              </div>
            </div>
          </div>


          {/* Usage Stats */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Minutes Used</span>
              <span className="text-gray-900 font-medium">
                {aiHostSubscription.minutes_used} / {aiHostSubscription.minutes_included} min
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${
                  aiHostSubscription.usage_percent > 90 ? 'bg-red-500' :
                  aiHostSubscription.usage_percent > 75 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(aiHostSubscription.usage_percent, 100)}%` }}
              />
            </div>
            <p className="text-gray-400 text-xs mt-1">
              {aiHostSubscription.minutes_remaining} minutes remaining this billing period
            </p>
          </div>

          {/* Billing Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Billing Period</p>
              <p className="text-gray-900 font-medium">
                {formatDate(aiHostSubscription.billing_period_start)} - {formatDate(aiHostSubscription.billing_period_end)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Estimated Bill</p>
              <p className="text-gray-900 text-lg font-medium">
                ${(aiHostSubscription.base_subscription_fee + aiHostSubscription.overage_cost).toFixed(2)}
              </p>
              <p className="text-gray-400 text-xs">Base fee + overage</p>
            </div>
          </div>

          {/* Detailed Usage */}
          <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Base Fee</p>
              <p className="text-gray-900 font-medium">${aiHostSubscription.base_subscription_fee.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Overage Minutes</p>
              {aiHostSubscription.overage_minutes > 0 ? (
                <>
                  <p className="text-[#b8860b] font-medium">${aiHostSubscription.overage_cost.toFixed(2)}</p>
                  <p className="text-gray-400 text-xs">{aiHostSubscription.overage_minutes} min @ $0.10/min</p>
                </>
              ) : (
                <>
                  <p className="text-green-600 font-medium">$0.00</p>
                  <p className="text-gray-400 text-xs">No overage this period</p>
                </>
              )}
            </div>
          </div>

          {/* Alerts */}
          {aiHostSubscription.alerts && aiHostSubscription.alerts.length > 0 && (
            <div className="space-y-2 mb-6">
              {aiHostSubscription.alerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                  <span className="text-yellow-700 text-sm">{alert}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action Button */}
          <Button
            onClick={handleManageSubscription}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Manage AI Host Subscription
          </Button>
        </div>
        )}

        {/* Plan Features - Only show for menu subscription */}
        {subscription && currentPlan && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Plan Features</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {getFeatureDisplayText(currentPlan).map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Billing History */}
        {!isDemoUser && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Billing History</h3>
              <Button
                size="sm"
                className="bg-transparent hover:bg-[#FDB913] text-gray-500 hover:text-black border-0 transition-colors"
                onClick={handleDownloadInvoices}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Invoices
              </Button>
            </div>
            <p className="text-gray-500 text-sm">
              Access your complete billing history and download invoices from the billing portal.
            </p>
          </div>
        )}
      </div>

      {/* Cancellation is now handled through Stripe Customer Portal */}

    </>
  );
};

export default SubscriptionManagement;